import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import { DndContext, closestCenter, useDraggable, useDroppable, type Collision, type CollisionDetection, type DragEndEvent } from '@dnd-kit/core';
import { Box, Button, FormControl, FormHelperText, IconButton, InputLabel, MenuItem, Select, Stack, Tooltip } from '@mui/material';
import type { TFunction } from 'i18next';
import { Fragment } from 'react';
import type React from 'react';
import type { ActionItem } from '../../../redux/EditorSlice';
import { getEnumOptions } from '../../../framework/framework-enums';
import { frameworkActionArgsAreValid } from '../../../utils/sequenceTupleValidation';
import { withResolvedMotorVelocityLimits } from '../../../utils/motorVelocityLimits';
import ActionSelector from '../../Drawers/Controller/ActionSelector';
import FrameworkActionArgumentFields from '../../Drawers/Controller/FrameworkActionArgumentFields';
import {
  AUTONOMOUS_GROUP_TYPES,
  type AutonomousGroupType,
  type AutonomousRoutineStep,
  findActionDefForLabel,
} from './driveModeAutonomousActionUtils';

const AUTONOMOUS_GROUP_LABELS: Record<AutonomousGroupType, { labelKey: string; defaultLabel: string }> = {
  SequentialActionGroup: {
    labelKey: 'driveModeConfig.autonomousRoutines.groupType.sequential',
    defaultLabel: 'Run in order',
  },
  ParallelActionGroup: {
    labelKey: 'driveModeConfig.autonomousRoutines.groupType.parallel',
    defaultLabel: 'Run together',
  },
  ParallelRaceGroup: {
    labelKey: 'driveModeConfig.autonomousRoutines.groupType.race',
    defaultLabel: 'Race until first finishes',
  },
  ParallelDeadlineGroup: {
    labelKey: 'driveModeConfig.autonomousRoutines.groupType.deadline',
    defaultLabel: 'Deadline until first step finishes',
  },
};

const VISIBLE_AUTONOMOUS_GROUP_TYPES: readonly AutonomousGroupType[] = AUTONOMOUS_GROUP_TYPES.slice(0, 2);

function routineGroupTypeOptions(currentGroupType: AutonomousGroupType): readonly AutonomousGroupType[] {
  return VISIBLE_AUTONOMOUS_GROUP_TYPES.includes(currentGroupType)
    ? VISIBLE_AUTONOMOUS_GROUP_TYPES
    : [...VISIBLE_AUTONOMOUS_GROUP_TYPES, currentGroupType];
}

export const createEmptyActionStep = (): AutonomousRoutineStep => ({
  kind: 'action',
  selection: '',
  argValues: {},
});

export const createEmptyGroupStep = (groupType: AutonomousGroupType = 'SequentialActionGroup'): AutonomousRoutineStep => ({
  kind: 'group',
  groupType,
  children: [createEmptyActionStep()],
});

export function updateStepAtPath(
  step: AutonomousRoutineStep,
  path: number[],
  updater: (target: AutonomousRoutineStep) => AutonomousRoutineStep
): AutonomousRoutineStep {
  if (path.length === 0) return updater(step);
  if (step.kind !== 'group') return step;
  const [childIndex, ...rest] = path;
  return {
    ...step,
    children: step.children.map((child, index) => (index === childIndex ? updateStepAtPath(child, rest, updater) : child)),
  };
}

export function removeStepAtPath(step: AutonomousRoutineStep, path: number[]): AutonomousRoutineStep {
  if (step.kind !== 'group' || path.length === 0) return step;
  const [childIndex, ...rest] = path;
  if (rest.length === 0) return { ...step, children: step.children.filter((_, index) => index !== childIndex) };
  return {
    ...step,
    children: step.children.map((child, index) => (index === childIndex ? removeStepAtPath(child, rest) : child)),
  };
}

export function appendChildAtPath(step: AutonomousRoutineStep, path: number[], child: AutonomousRoutineStep): AutonomousRoutineStep {
  if (path.length === 0 && step.kind === 'group') return { ...step, children: [...step.children, child] };
  if (step.kind !== 'group') return step;
  const [childIndex, ...rest] = path;
  return {
    ...step,
    children: step.children.map((existing, index) => (index === childIndex ? appendChildAtPath(existing, rest, child) : existing)),
  };
}

export interface RoutineStepDropPosition {
  parentPath: number[];
  index: number;
}

type RoutineStepMoveDirection = 'up' | 'down';
type AutonomousRoutineGroupStep = Extract<AutonomousRoutineStep, { kind: 'group' }>;

function pathsEqual(a: readonly number[], b: readonly number[]): boolean {
  return a.length === b.length && a.every((part, index) => part === b[index]);
}

function pathStartsWith(path: readonly number[], prefix: readonly number[]): boolean {
  return prefix.length <= path.length && prefix.every((part, index) => path[index] === part);
}

function getStepAtPath(step: AutonomousRoutineStep, path: readonly number[]): AutonomousRoutineStep | null {
  if (path.length === 0) return step;
  if (step.kind !== 'group') return null;
  const [childIndex, ...rest] = path;
  const child = step.children[childIndex];
  return child ? getStepAtPath(child, rest) : null;
}

function getParentGroupAtPath(step: AutonomousRoutineStep, path: readonly number[]): AutonomousRoutineGroupStep | null {
  const parent = getStepAtPath(step, path);
  return parent?.kind === 'group' ? parent : null;
}

function removeStepWithNode(
  step: AutonomousRoutineStep,
  path: readonly number[]
): { next: AutonomousRoutineStep; removed: AutonomousRoutineStep | null } {
  if (step.kind !== 'group' || path.length === 0) return { next: step, removed: null };
  const [childIndex, ...rest] = path;
  if (rest.length === 0) {
    const removed = step.children[childIndex] ?? null;
    return { next: { ...step, children: step.children.filter((_, index) => index !== childIndex) }, removed };
  }
  const child = step.children[childIndex];
  if (!child) return { next: step, removed: null };
  const result = removeStepWithNode(child, rest);
  return {
    next: {
      ...step,
      children: step.children.map((existing, index) => (index === childIndex ? result.next : existing)),
    },
    removed: result.removed,
  };
}

function insertStepAtPosition(
  step: AutonomousRoutineStep,
  destination: RoutineStepDropPosition,
  child: AutonomousRoutineStep
): AutonomousRoutineStep {
  if (destination.parentPath.length === 0) {
    if (step.kind !== 'group') return step;
    const insertIndex = Math.max(0, Math.min(destination.index, step.children.length));
    return {
      ...step,
      children: [...step.children.slice(0, insertIndex), child, ...step.children.slice(insertIndex)],
    };
  }
  if (step.kind !== 'group') return step;
  const [childIndex, ...rest] = destination.parentPath;
  return {
    ...step,
    children: step.children.map((existing, index) =>
      index === childIndex ? insertStepAtPosition(existing, { parentPath: rest, index: destination.index }, child) : existing
    ),
  };
}

function adjustPathAfterRemoval(path: number[], sourcePath: readonly number[]): number[] {
  const sourceParentPath = sourcePath.slice(0, -1);
  const sourceIndex = sourcePath[sourcePath.length - 1];
  if (
    path.length > sourceParentPath.length &&
    pathsEqual(path.slice(0, sourceParentPath.length), sourceParentPath) &&
    path[sourceParentPath.length] > sourceIndex
  ) {
    const next = [...path];
    next[sourceParentPath.length] -= 1;
    return next;
  }
  return path;
}

function adjustDestinationAfterRemoval(destination: RoutineStepDropPosition, sourcePath: readonly number[]): RoutineStepDropPosition {
  const sourceParentPath = sourcePath.slice(0, -1);
  const sourceIndex = sourcePath[sourcePath.length - 1];
  const parentPath = adjustPathAfterRemoval(destination.parentPath, sourcePath);
  const index = pathsEqual(destination.parentPath, sourceParentPath) && sourceIndex < destination.index ? destination.index - 1 : destination.index;
  return { parentPath, index };
}

export function canMoveStepToPosition(step: AutonomousRoutineStep, sourcePath: readonly number[], destination: RoutineStepDropPosition): boolean {
  if (sourcePath.length === 0) return false;
  if (pathStartsWith(destination.parentPath, sourcePath)) return false;
  if (!getStepAtPath(step, sourcePath)) return false;
  if (!getParentGroupAtPath(step, destination.parentPath)) return false;
  const destinationParent = getParentGroupAtPath(step, destination.parentPath);
  return Boolean(destinationParent && destination.index >= 0 && destination.index <= destinationParent.children.length);
}

export function moveStepToPosition(
  step: AutonomousRoutineStep,
  sourcePath: readonly number[],
  destination: RoutineStepDropPosition
): AutonomousRoutineStep {
  if (!canMoveStepToPosition(step, sourcePath, destination)) return step;
  const { next, removed } = removeStepWithNode(step, sourcePath);
  if (!removed) return step;
  return insertStepAtPosition(next, adjustDestinationAfterRemoval(destination, sourcePath), removed);
}

export function resolveRoutineStepMoveDestination(
  step: AutonomousRoutineStep,
  path: readonly number[],
  direction: RoutineStepMoveDirection
): RoutineStepDropPosition | null {
  if (path.length === 0) return null;
  const parentPath = path.slice(0, -1);
  const index = path[path.length - 1];
  const parent = getParentGroupAtPath(step, parentPath);
  if (!parent) return null;

  if (direction === 'up') {
    if (index > 0) return { parentPath, index: index - 1 };
    if (parentPath.length === 0) return null;
    return { parentPath: parentPath.slice(0, -1), index: parentPath[parentPath.length - 1] };
  }

  if (index < parent.children.length - 1) return { parentPath, index: index + 2 };
  if (parentPath.length === 0) return null;
  return { parentPath: parentPath.slice(0, -1), index: parentPath[parentPath.length - 1] + 1 };
}

export function canMoveStepInDirection(step: AutonomousRoutineStep, path: readonly number[], direction: RoutineStepMoveDirection): boolean {
  const destination = resolveRoutineStepMoveDestination(step, path, direction);
  return Boolean(destination && canMoveStepToPosition(step, path, destination));
}

export function hasUnselectedActionStep(step: AutonomousRoutineStep): boolean {
  if (step.kind === 'action') return !step.selection;
  return step.children.some(hasUnselectedActionStep);
}

export function pruneRoutineStep(step: AutonomousRoutineStep): AutonomousRoutineStep | null {
  if (step.kind === 'action') return step.selection ? step : null;
  const children = step.children.flatMap((child) => {
    const pruned = pruneRoutineStep(child);
    return pruned ? [pruned] : [];
  });
  return children.length > 0 ? { ...step, children } : null;
}

export function snapshotRoutineDraft(identifier: string, composition: AutonomousRoutineStep | null): string {
  return JSON.stringify({ identifier, composition });
}

export function routineStepArgsAreValid(step: AutonomousRoutineStep, actionList: ActionItem[], t: TFunction): boolean {
  if (step.kind === 'group') return step.children.every((child) => routineStepArgsAreValid(child, actionList, t));
  if (!step.selection) return false;

  const actionDef = findActionDefForLabel(actionList, step.selection);
  return frameworkActionArgsAreValid(actionDef, step.argValues, t);
}

interface RoutineStepEditorProps {
  step: AutonomousRoutineStep;
  path: number[];
  canDelete: boolean;
  actionList: ActionItem[];
  pathOptions: Array<{ id: string; name: string }>;
  enumOptions: ReturnType<typeof getEnumOptions>;
  readOnly: boolean;
  t: TFunction;
  normalizeArgValuesForAction: (selection: string, values: Record<string, string>) => Record<string, string>;
  initialArgValuesForSelection: (selection: string) => Record<string, string>;
  onChange: (path: number[], updater: (target: AutonomousRoutineStep) => AutonomousRoutineStep) => void;
  onAppendChild: (path: number[], child: AutonomousRoutineStep) => void;
  onDelete: (path: number[]) => void;
  onMove: (path: number[], destination: RoutineStepDropPosition) => void;
}

type RoutineStepEditorInnerProps = RoutineStepEditorProps & {
  rootStep: AutonomousRoutineStep;
};

interface RoutineDropZoneProps {
  parentPath: number[];
  index: number;
  readOnly: boolean;
}

const routinePathLabel = (path: readonly number[]) => path.map((part) => part + 1).join('.');

export const routineStepCollisionDetection: CollisionDetection = (args) => {
  const { droppableContainers, droppableRects, pointerCoordinates } = args;
  if (!pointerCoordinates) return closestCenter(args);

  const collisions: Collision[] = [];
  for (const droppableContainer of droppableContainers) {
    const rect = droppableRects.get(droppableContainer.id);
    if (!rect) continue;

    const parentPath = droppableContainer.data.current?.parentPath;
    const depth = Array.isArray(parentPath) ? parentPath.length : 0;
    const centerY = rect.top + rect.height / 2;
    const verticalDistance = Math.abs(pointerCoordinates.y - centerY);
    const horizontalDistance = pointerCoordinates.x < rect.left ? rect.left - pointerCoordinates.x : Math.max(0, pointerCoordinates.x - rect.right);

    collisions.push({
      id: droppableContainer.id,
      data: {
        droppableContainer,
        value: verticalDistance + horizontalDistance * 0.01 - depth * 0.001,
      },
    });
  }

  return collisions.sort((a, b) => (a.data?.value ?? 0) - (b.data?.value ?? 0));
};

const RoutineDropZone: React.FC<RoutineDropZoneProps> = ({ parentPath, index, readOnly }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: `routine-drop-${parentPath.join('-') || 'root'}-${index}`,
    data: { parentPath, index },
    disabled: readOnly,
  });

  return (
    <Box
      ref={setNodeRef}
      data-testid={`routine-drop-${parentPath.join('.') || 'root'}-${index}`}
      sx={{
        minHeight: 8,
        borderRadius: 1,
        bgcolor: isOver ? 'action.selected' : 'transparent',
      }}
    />
  );
};

const RoutineStepEditorInner: React.FC<RoutineStepEditorInnerProps> = ({
  step,
  path,
  canDelete,
  actionList,
  pathOptions,
  enumOptions,
  readOnly,
  t,
  normalizeArgValuesForAction,
  initialArgValuesForSelection,
  onChange,
  onAppendChild,
  onDelete,
  onMove,
  rootStep,
}) => {
  const pathLabel = routinePathLabel(path);
  const isRoot = path.length === 0;
  const moveUpLabel = t(
    step.kind === 'group' ? 'driveModeConfig.autonomousRoutines.moveGroupUpAria' : 'driveModeConfig.autonomousRoutines.moveStepUpAria',
    {
      index: pathLabel,
      defaultValue: step.kind === 'group' ? 'move routine group {{index}} up' : 'move routine step {{index}} up',
    }
  );
  const moveDownLabel = t(
    step.kind === 'group' ? 'driveModeConfig.autonomousRoutines.moveGroupDownAria' : 'driveModeConfig.autonomousRoutines.moveStepDownAria',
    {
      index: pathLabel,
      defaultValue: step.kind === 'group' ? 'move routine group {{index}} down' : 'move routine step {{index}} down',
    }
  );
  const dragLabel = t(
    step.kind === 'group' ? 'driveModeConfig.autonomousRoutines.dragGroupAria' : 'driveModeConfig.autonomousRoutines.dragStepAria',
    {
      index: pathLabel,
      defaultValue: step.kind === 'group' ? 'drag routine group {{index}}' : 'drag routine step {{index}}',
    }
  );
  const canMoveUp = !readOnly && canMoveStepInDirection(rootStep, path, 'up');
  const canMoveDown = !readOnly && canMoveStepInDirection(rootStep, path, 'down');
  const handleMoveInDirection = (direction: RoutineStepMoveDirection) => {
    const destination = resolveRoutineStepMoveDestination(rootStep, path, direction);
    if (!destination) return;
    onMove(path, destination);
  };
  const { attributes, listeners, setActivatorNodeRef, setNodeRef, isDragging } = useDraggable({
    id: `routine-step-${path.join('-')}`,
    data: { path },
    disabled: readOnly || isRoot,
  });

  const moveControls = !isRoot ? (
    <>
      <Tooltip title={dragLabel}>
        <span>
          <IconButton
            aria-label={dragLabel}
            size="small"
            ref={setActivatorNodeRef}
            disabled={readOnly}
            {...listeners}
            {...attributes}
            sx={{ cursor: readOnly ? 'default' : 'grab' }}
          >
            <DragIndicatorIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={moveUpLabel}>
        <span>
          <IconButton aria-label={moveUpLabel} size="small" onClick={() => handleMoveInDirection('up')} disabled={!canMoveUp}>
            <KeyboardArrowUpIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
      <Tooltip title={moveDownLabel}>
        <span>
          <IconButton aria-label={moveDownLabel} size="small" onClick={() => handleMoveInDirection('down')} disabled={!canMoveDown}>
            <KeyboardArrowDownIcon fontSize="small" />
          </IconButton>
        </span>
      </Tooltip>
    </>
  ) : null;

  if (step.kind === 'action') {
    const actionDef = findActionDefForLabel(actionList, step.selection);
    const effectiveActionDef = withResolvedMotorVelocityLimits(actionDef, step.argValues);
    return (
      <Stack ref={setNodeRef} spacing={1} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, opacity: isDragging ? 0.5 : 1 }}>
        <Stack direction="row" spacing={1} alignItems="center">
          {moveControls}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <ActionSelector
              value={step.selection}
              onChange={(selection) =>
                onChange(path, () => ({
                  kind: 'action',
                  selection,
                  argValues: normalizeArgValuesForAction(selection, initialArgValuesForSelection(selection)),
                }))
              }
              actions={actionList}
              allowNone
              noneLabel={t('controller.buttonMapping.none', { defaultValue: 'None' })}
              ariaLabel={t('driveModeConfig.autonomousRoutines.stepAria', {
                index: pathLabel,
                defaultValue: 'routine step {{index}}',
              })}
            />
          </Box>
          {canDelete && (
            <Tooltip
              title={t('driveModeConfig.autonomousRoutines.deleteStepAria', {
                index: pathLabel,
                defaultValue: 'delete routine step {{index}}',
              })}
            >
              <span>
                <IconButton
                  aria-label={t('driveModeConfig.autonomousRoutines.deleteStepAria', {
                    index: pathLabel,
                    defaultValue: 'delete routine step {{index}}',
                  })}
                  size="small"
                  onClick={() => onDelete(path)}
                  disabled={readOnly}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          )}
        </Stack>
        <FrameworkActionArgumentFields
          actionDef={effectiveActionDef}
          values={step.argValues}
          onArgChange={(name, value) =>
            onChange(path, (target) => (target.kind === 'action' ? { ...target, argValues: { ...target.argValues, [name]: value } } : target))
          }
          onArgValuesChange={(next) => onChange(path, (target) => (target.kind === 'action' ? { ...target, argValues: next } : target))}
          pathOptions={pathOptions}
          enumOptions={enumOptions}
          t={t}
          layout="stack"
          showUnits={false}
          spacing={1}
          readOnly={readOnly}
        />
      </Stack>
    );
  }

  const groupTypeOptions = routineGroupTypeOptions(step.groupType);

  return (
    <Stack ref={setNodeRef} spacing={1} sx={{ border: 1, borderColor: 'divider', borderRadius: 1, p: 1, opacity: isDragging ? 0.5 : 1 }}>
      <Stack direction="row" spacing={1} alignItems="center">
        {moveControls}
        <FormControl size="small" sx={{ flex: 1, minWidth: 0 }}>
          <InputLabel id={`routine-group-type-${path.join('-') || 'root'}-label`}>
            {t('driveModeConfig.autonomousRoutines.groupType', { defaultValue: 'Group behavior' })}
          </InputLabel>
          <Select
            labelId={`routine-group-type-${path.join('-') || 'root'}-label`}
            label={t('driveModeConfig.autonomousRoutines.groupType', { defaultValue: 'Group behavior' })}
            value={step.groupType}
            onChange={(event) =>
              onChange(path, (target) => (target.kind === 'group' ? { ...target, groupType: event.target.value as AutonomousGroupType } : target))
            }
            disabled={readOnly}
          >
            {groupTypeOptions.map((groupType) => {
              const groupLabel = AUTONOMOUS_GROUP_LABELS[groupType];
              return (
                <MenuItem key={groupType} value={groupType}>
                  {t(groupLabel.labelKey, { defaultValue: groupLabel.defaultLabel })}
                </MenuItem>
              );
            })}
          </Select>
          {step.groupType === 'ParallelDeadlineGroup' && (
            <FormHelperText>
              {t('driveModeConfig.autonomousRoutines.deadlineHelp', { defaultValue: 'The first step is the deadline.' })}
            </FormHelperText>
          )}
        </FormControl>
        {canDelete && (
          <Tooltip
            title={t('driveModeConfig.autonomousRoutines.deleteGroupAria', {
              index: pathLabel,
              defaultValue: 'delete routine group {{index}}',
            })}
          >
            <span>
              <IconButton
                aria-label={t('driveModeConfig.autonomousRoutines.deleteGroupAria', {
                  index: pathLabel,
                  defaultValue: 'delete routine group {{index}}',
                })}
                size="small"
                onClick={() => onDelete(path)}
                disabled={readOnly}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        )}
      </Stack>
      <Stack spacing={1}>
        <RoutineDropZone parentPath={path} index={0} readOnly={readOnly} />
        {step.children.map((child, index) => (
          <Fragment key={`${path.join('.')}-${index}`}>
            <RoutineStepEditorInner
              step={child}
              path={[...path, index]}
              canDelete={step.children.length > 1}
              actionList={actionList}
              pathOptions={pathOptions}
              enumOptions={enumOptions}
              readOnly={readOnly}
              t={t}
              normalizeArgValuesForAction={normalizeArgValuesForAction}
              initialArgValuesForSelection={initialArgValuesForSelection}
              onChange={onChange}
              onAppendChild={onAppendChild}
              onDelete={onDelete}
              onMove={onMove}
              rootStep={rootStep}
            />
            <RoutineDropZone parentPath={path} index={index + 1} readOnly={readOnly} />
          </Fragment>
        ))}
      </Stack>
      <Stack direction="row" spacing={1} flexWrap="wrap">
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAppendChild(path, createEmptyActionStep())}
          disabled={readOnly || hasUnselectedActionStep(step)}
        >
          {t('driveModeConfig.autonomousRoutines.addStep', { defaultValue: 'ADD STEP' })}
        </Button>
        <Button
          size="small"
          startIcon={<AddIcon />}
          onClick={() => onAppendChild(path, createEmptyGroupStep())}
          disabled={readOnly || hasUnselectedActionStep(step)}
        >
          {t('driveModeConfig.autonomousRoutines.addGroup', { defaultValue: 'ADD GROUP' })}
        </Button>
      </Stack>
    </Stack>
  );
};

const RoutineStepEditor: React.FC<RoutineStepEditorProps> = (props) => {
  const handleDragEnd = (event: DragEndEvent) => {
    const sourcePath = event.active.data.current?.path;
    const destination = event.over?.data.current;
    if (!Array.isArray(sourcePath) || !destination || !Array.isArray(destination.parentPath) || typeof destination.index !== 'number') return;
    props.onMove(sourcePath, { parentPath: destination.parentPath, index: destination.index });
  };

  return (
    <DndContext collisionDetection={routineStepCollisionDetection} onDragEnd={handleDragEnd}>
      <RoutineStepEditorInner {...props} rootStep={props.step} />
    </DndContext>
  );
};

export default RoutineStepEditor;
